import React from 'react';
import { ImplementationRoadmap } from '../../../types';

interface RoadmapSectionProps {
  data: ImplementationRoadmap;
}

export const RoadmapSection: React.FC<RoadmapSectionProps> = ({ data }) => {
  const getPhaseIcon = (phase: string) => {
    const icons: Record<string, string> = {
      discovery: '🔍',
      design: '🎨',
      development: '⚙️',
      testing: '🧪',
      deployment: '🚀',
      training: '📚',
      support: '🛟',
    };
    return icons[phase.toLowerCase()] || '📋';
  };

  const getPhaseColor = (phase: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      discovery: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
      design: { bg: '#e9d5ff', text: '#7e22ce', border: '#d8b4fe' },
      development: { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
      testing: { bg: '#fef3c7', text: '#92400e', border: '#fde047' },
      deployment: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
      training: { bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc' },
      support: { bg: '#fce7f3', text: '#9f1239', border: '#f9a8d4' },
    };
    return colors[phase.toLowerCase()] || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
  };

  return (
    <div style={{ marginBottom: '3rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
          Implementation Roadmap
        </h2>
        <p style={{ color: '#6b7280' }}>
          Phased approach to deliver your AI/automation solution
        </p>
      </div>

      {/* Overview */}
      <div style={{ 
        background: 'linear-gradient(to right, #3b82f6, #9333ea)', 
        color: '#ffffff', 
        borderRadius: '12px', 
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1.5rem' }}>
          <div>
            <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.25rem' }}>Total Duration</p>
            <p style={{ fontSize: '2rem', fontWeight: 'bold' }}>{data.total_duration_weeks} weeks</p>
          </div>
          <div>
            <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.25rem' }}>Complexity</p>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', textTransform: 'capitalize' }}>
              {data.complexity.replace('_', ' ')}
            </p>
          </div>
          <div>
            <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.25rem' }}>Milestones</p>
            <p style={{ fontSize: '2rem', fontWeight: 'bold' }}>{data.milestones.length}</p>
          </div>
        </div>
      </div>

      {/* Quick Wins */}
      {data.quick_wins.length > 0 && (
        <div style={{ 
          background: '#dcfce7', 
          border: '1px solid #86efac', 
          borderRadius: '12px', 
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#14532d', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '0.5rem' }}>⚡</span>
            Quick Wins
          </h3>
          <p style={{ fontSize: '0.875rem', color: '#15803d', marginBottom: '1rem' }}>
            Early deliverables that provide immediate value
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            {data.quick_wins.map((win, idx) => (
              <div key={idx} style={{ background: '#ffffff', border: '1px solid #86efac', borderRadius: '8px', padding: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: '600', color: '#111827' }}>{win.name}</span>
                  <span style={{ 
                    fontSize: '0.75rem', 
                    padding: '0.25rem 0.5rem', 
                    background: '#dcfce7', 
                    color: '#14532d', 
                    borderRadius: '4px', 
                    fontWeight: '500' 
                  }}>
                    Week {win.delivery_week}
                  </span>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>{win.impact}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1.5rem' }}>Implementation Timeline</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {data.milestones.map((milestone, idx) => {
            const phaseColors = getPhaseColor(milestone.phase);
            return (
              <div key={idx} style={{ position: 'relative' }}>
                {/* Connector line */}
                {idx < data.milestones.length - 1 && (
                  <div style={{ 
                    position: 'absolute', 
                    left: '24px', 
                    top: '48px', 
                    bottom: '-24px', 
                    width: '2px', 
                    background: '#d1d5db' 
                  }} />
                )}

                <div style={{ display: 'flex', gap: '1rem' }}>
                  {/* Phase Icon */}
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '9999px', 
                      border: `2px solid ${phaseColors.border}`, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '1.5rem',
                      background: phaseColors.bg,
                      color: phaseColors.text
                    }}>
                      {getPhaseIcon(milestone.phase)}
                    </div>
                  </div>

                  {/* Milestone Content */}
                  <div style={{ 
                    flex: 1, 
                    background: '#f9fafb', 
                    borderRadius: '8px', 
                    padding: '1rem',
                    transition: 'box-shadow 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <div>
                        <h4 style={{ fontWeight: 'bold', color: '#111827', fontSize: '1.125rem' }}>{milestone.name}</h4>
                        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>{milestone.description}</p>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                        <span style={{ 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '4px', 
                          fontSize: '0.75rem', 
                          fontWeight: '500',
                          background: phaseColors.bg,
                          color: phaseColors.text,
                          border: `1px solid ${phaseColors.border}`
                        }}>
                          {milestone.phase}
                        </span>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          {milestone.duration_weeks} {milestone.duration_weeks === 1 ? 'week' : 'weeks'}
                        </span>
                      </div>
                    </div>

                    {/* Deliverables */}
                    {milestone.deliverables.length > 0 && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <p style={{ fontSize: '0.75rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>Deliverables:</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {milestone.deliverables.map((deliverable, didx) => (
                            <span
                              key={didx}
                              style={{ 
                                padding: '0.25rem 0.5rem', 
                                background: '#ffffff', 
                                border: '1px solid #e5e7eb', 
                                borderRadius: '4px', 
                                fontSize: '0.75rem', 
                                color: '#374151' 
                              }}
                            >
                              ✓ {deliverable}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Success Criteria */}
                    {milestone.success_criteria.length > 0 && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <p style={{ fontSize: '0.75rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>Success Criteria:</p>
                        <ul style={{ listStyle: 'disc', listStylePosition: 'inside', fontSize: '0.75rem', color: '#6b7280', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          {milestone.success_criteria.map((criteria, cidx) => (
                            <li key={cidx}>{criteria}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Dependencies */}
                    {milestone.depends_on.length > 0 && (
                      <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          <span style={{ fontWeight: '500' }}>Depends on:</span> {milestone.depends_on.join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Key Risks */}
      {data.key_risks.length > 0 && (
        <div style={{ 
          background: '#fef3c7', 
          border: '1px solid #fde047', 
          borderRadius: '12px', 
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#92400e', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '0.5rem' }}>⚠️</span>
            Key Risks & Mitigation
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {data.key_risks.map((risk, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', background: '#ffffff', borderRadius: '8px', padding: '0.75rem' }}>
                <span style={{ color: '#ca8a04', fontWeight: 'bold', fontSize: '1.125rem' }}>⚠️</span>
                <p style={{ color: '#374151', flex: 1 }}>{risk}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team Composition */}
      {Object.keys(data.team_composition).length > 0 && (
        <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '0.5rem' }}>👥</span>
            Required Team
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
            {Object.entries(data.team_composition).map(([role, allocation]) => (
              <div key={role} style={{ background: '#f9fafb', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2563eb' }}>{allocation}</p>
                <p style={{ fontSize: '0.875rem', color: '#374151', marginTop: '0.25rem', textTransform: 'capitalize' }}>
                  {role.replace(/_/g, ' ')}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

